import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { useDispatch, useSelector } from 'react-redux';
import { submitOnboardingForms, getOnboardingFormsDetails, resetSubmitState } from './onboardingFormsSlice';
import { ReviewProps } from './typesIN';
import SignatureInput from './SignatureInput';

const capitalize = (v: string): string => !v ? '' : v.charAt(0).toUpperCase() + v.slice(1);
const withPercent = (v: any): string => { const s = String(v || ''); return !s ? '' : s.includes('%') ? s : `${s}%`; };

const Review: React.FC<ReviewProps> = ({
    form2Data, gratuityData, form11Data, form2Nominees = [], gratuityNominees = [],
    formLData, isFemale = false,
    onBack, onSubmitSuccess
}) => {
    const dispatch = useDispatch();
    const { submitState, submitData, submitError } = useSelector(getOnboardingFormsDetails);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [employeeName, setEmployeeName] = useState('');
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [dobConfirm, setDobConfirm] = useState<Date | undefined>(undefined);
    const [dobError, setDobError] = useState('');
    const [submitSignature, setSubmitSignature] = useState('');
    const [submissionTime, setSubmissionTime] = useState<Date | null>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (submitState === 'fulfilled' && submitData) {
            setSubmissionTime(new Date());
            setShowConfirmDialog(false);
            timerRef.current = setTimeout(() => setShowSuccessDialog(true), 300);
        }
        if (submitState === 'failed' && submitError)
            alert(`Error submitting forms: ${typeof submitError === 'string' ? submitError : 'Please try again.'}`);
    }, [submitState, submitData, submitError]);

    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            dispatch(resetSubmitState());
        };
    }, [dispatch]);

    const formatDate = (date: any): string => {
        if (!date || typeof date === 'number' || typeof date === 'boolean') return '';
        const d = typeof date === 'string' ? new Date(date) : date;
        if (!(d instanceof Date) || isNaN(d.getTime())) return '';
        return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    };

    const getValue = (value: any, fallback = ''): string => {
        if (!value) return fallback;
        if (value instanceof Date) return formatDate(value);
        return String(value) || fallback;
    };

    const ReviewItem = ({ label, value, isCapitalized = false, isSigned = false }: {
        label: string; value: any; isCapitalized?: boolean; isSigned?: boolean;
    }) => {
        const display = isSigned ? '✓ Signed' : (isCapitalized ? capitalize(getValue(value)) : getValue(value));
        const isEmpty = !display && !isSigned;
        return (
            <div className="review-item">
                <label>{label}</label>
                <p className={isSigned ? 'signed' : isEmpty ? 'empty-value' : ''}>
                    {isSigned ? '✓ Signed' : (display || '—')}
                </p>
            </div>
        );
    };

    const validateDobConfirm = (date: Date | undefined): boolean => {
        if (!date) { setDobError('Date of Birth is required'); return false; }
        const profileDob = form2Data?.dob;
        if (profileDob) {
            const stored = profileDob instanceof Date ? profileDob : new Date(String(profileDob));
            const match =
                stored.getDate() === date.getDate() &&
                stored.getMonth() === date.getMonth() &&
                stored.getFullYear() === date.getFullYear();
            if (!match) { setDobError('Date of Birth does not match our records'); return false; }
        }
        setDobError('');
        return true;
    };

    const handleConfirmSubmit = () => {
        if (!isConfirmed || !employeeName) return;
        if (!validateDobConfirm(dobConfirm)) return;
        if (!submitSignature.trim()) return;

        const timestamp = new Date();
        const stepId = localStorage.getItem('digitalFormStepId');
        const oracleId = localStorage.getItem('digitalFormOracleId');
        const employeeUid = localStorage.getItem('digitalFormEmployeeUid');

        const formsData: any = {
            form2: {
                oracle_id: form2Data?.oracleId, uan: form2Data?.uan, full_name: form2Data?.fullName,
                father_husband_name: form2Data?.fatherName,
                date_of_birth: form2Data?.dob, gender: form2Data?.gender,
                marital_status: form2Data?.maritalStatus, pf_account_no: form2Data?.pfAccount,
                date_of_joining: form2Data?.joiningDate, permanent_address: form2Data?.permanentAddress,
                temporary_address: form2Data?.temporaryAddress,
                nominee_name: form2Data?.nomineeName, nominee_address: form2Data?.nomineeAddress,
                nominee_relationship: form2Data?.nomineeRelationship,
                nominee_dob: form2Data?.nomineeDob,
                nominee_age: form2Data?.nomineeAge ? Number(form2Data.nomineeAge) : null,
                pf_amount: '100', guardian_info: null,
                family_member_name: form2Data?.familyMemberName, family_address: form2Data?.familyAddress,
                family_dob: form2Data?.familyDob,
                family_member_age: form2Data?.familyAge ? Number(form2Data.familyAge) : null,
                family_relationship: form2Data?.familyRelationship,
                subscriber_signature: form2Data?.subscriberSignature,
                subscriber_signature_b: form2Data?.subscriberSignatureB,
                employer_signature: form2Data?.employerSignature, acknowledgement: true,
                place: form2Data?.place, date: form2Data?.date,
                number_of_additional_family_members: form2Nominees.length,
                additional_family_members: form2Nominees.map((m, i) => ({
                    sequence: i + 2, family_member_name: m.familyMemberName,
                    family_address: m.familyAddress, family_dob: m.familyDob,
                    family_member_age: m.familyAge ? Number(m.familyAge) : null,
                    family_relationship: m.familyRelationship,
                })),
                submitted_by: employeeName, submission_timestamp: timestamp,
            },
            gratuity: {
                to_name: gratuityData?.toName, address: gratuityData?.address,
                prefix: gratuityData?.prefix,
                nominee_full_name: gratuityData?.nomineeFullName, nominee_name: gratuityData?.nomineeName,
                nominee_address: gratuityData?.nomineeAddress, nominee_dob: gratuityData?.nomineeDob,
                nominee_age: gratuityData?.nomineeAge ? Number(gratuityData.nomineeAge) : null,
                relationship: gratuityData?.relationship, proportion: gratuityData?.proportion,
                employee_name: gratuityData?.employeeName, gender: gratuityData?.gender,
                religion: gratuityData?.religion, marital_status: gratuityData?.maritalStatus,
                department: gratuityData?.department, post_held: gratuityData?.postHeld,
                appointment_date: gratuityData?.appointmentDate,
                permanent_address: gratuityData?.permanentAddress,
                place: gratuityData?.place, date: gratuityData?.finalDate,
                final_date: gratuityData?.finalDate,
                subscriber_signature: gratuityData?.subscriberSignature, acknowledgement: true,
                joining_location: gratuityData?.joiningLocation,
                establishment_address: gratuityData?.establishmentAddress,
                designation: gratuityData?.designation, employer_signature: gratuityData?.employerSignature,
                number_of_additional_nominees: gratuityNominees.length,
                additional_nominees: gratuityNominees.map((n, i) => ({
                    sequence: i + 2, prefix: n.prefix, nominee_full_name: n.fullName,
                    nominee_name: n.name, nominee_address: n.address,
                    nominee_dob: n.dob,
                    nominee_age: n.age ? Number(n.age) : null,
                    relationship: n.relationship, proportion: n.proportion,
                })),
                submitted_by: employeeName, submission_timestamp: timestamp,
            },
            form11: {
                member_name: form11Data?.memberName, father_husband_name: form11Data?.fatherName,
                date_of_birth: form11Data?.dob, gender: form11Data?.gender,
                marital_status: form11Data?.maritalStatus, email: form11Data?.email,
                mobile: form11Data?.mobile, epf_member: form11Data?.epfMember,
                eps_member: form11Data?.epsMember, uan: form11Data?.uan,
                previous_pf: form11Data?.previousPF, exit_date: form11Data?.exitDate,
                international_worker: form11Data?.internationalWorker, country: form11Data?.country,
                passport_number: form11Data?.passport, passport_validity: form11Data?.passportValidity,
                bank_account: form11Data?.bankAccount, ifsc_code: form11Data?.ifsc,
                aadhar_number: form11Data?.aadhar, pan_number: form11Data?.pan,
                place: form11Data?.place, date: form11Data?.date,
                member_signature: form11Data?.memberSignature,
                member_name_confirm: form11Data?.memberNameConfirm,
                signature_date: form11Data?.signatureDate, joined_on: form11Data?.joinedOn,
                employer_signature: form11Data?.employerSignature,
                submitted_by: employeeName, submission_timestamp: timestamp,
            },
        };

        // ── Include formL payload ONLY for female employees ──────────────────
        if (isFemale && formLData) {
            formsData.formL = {
                oracle_id: form2Data?.oracleId,
                full_name: formLData?.fullName,
                full_address: formLData?.fullAddress,
                designation: formLData?.designation,
                date_of_joining: formLData?.dateOfJoining,
                working_location: formLData?.workingLocation,
                posh_chairperson_name: formLData?.poshChairpersonName,
                employee_signature: formLData?.employeeSignature,
                acknowledgement: true,
                witness1_signature: formLData?.witness1Signature || '',
                witness2_signature: formLData?.witness2Signature || '',
                submitted_by: employeeName,
                submission_timestamp: timestamp,
            };
        }

        const serializeDates = (obj: any): any => {
            if (obj instanceof Date) return obj.toISOString();
            if (Array.isArray(obj)) return obj.map(serializeDates);
            if (obj && typeof obj === 'object')
                return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, serializeDates(v)]));
            return obj;
        };

        dispatch(submitOnboardingForms({
            action: 'submitOnboardingForms',
            oracleId: oracleId || '',
            id: stepId || '',
            employeeUid: employeeUid || '',
            requestData: serializeDates(formsData),
        }));
    };

    const handleSuccessClose = () => {
        setShowSuccessDialog(false);
        dispatch(resetSubmitState());
        onSubmitSuccess?.();
    };

    return (
        <div className="review-container">
            <h1 className="review-main-title">Onboarding Digital Form</h1>
            <h3 className="review-subtitle">Review &amp; Submit</h3>

            {/* ── FORM 2 ── */}
            <div className="review-section">
                <h2 className="section-title">Form 2 — EPF Nomination</h2>
                <div className="review-section-body">
                    <div className="review-row-5">
                        <ReviewItem label="Oracle ID" value={form2Data?.oracleId} />
                        <ReviewItem label="UAN" value={form2Data?.uan} />
                        <ReviewItem label="Full Name" value={form2Data?.fullName} />
                        <ReviewItem label="Father/Husband's Name" value={form2Data?.fatherName} />
                        <ReviewItem label="Date of Birth" value={formatDate(form2Data?.dob)} />
                    </div>
                    <div className="review-row-5">
                        <ReviewItem label="Gender" value={form2Data?.gender} isCapitalized />
                        <ReviewItem label="Marital Status" value={form2Data?.maritalStatus} isCapitalized />
                        <ReviewItem label="PF Account No." value={form2Data?.pfAccount} />
                        <ReviewItem label="Date of Joining" value={formatDate(form2Data?.joiningDate)} />
                    </div>
                    <div className="review-row-2">
                        <ReviewItem label="Permanent Address" value={form2Data?.permanentAddress} />
                        <ReviewItem label="Temporary Address" value={form2Data?.temporaryAddress} />
                    </div>
                    <div className="section-divider" />
                    <div className="nominee-header"><strong>Part A — EPF Nominee</strong></div>
                    <div className="review-row-5">
                        <ReviewItem label="Nominee's Name" value={form2Data?.nomineeName} />
                        <ReviewItem label="Relationship" value={form2Data?.nomineeRelationship} isCapitalized />
                        <ReviewItem label="Date of Birth" value={formatDate(form2Data?.nomineeDob)} />
                        <ReviewItem label="Age" value={form2Data?.nomineeAge} />
                        <ReviewItem label="PF Share" value="100%" />
                    </div>
                    <div className="review-row-2">
                        <ReviewItem label="Nominee's Address" value={form2Data?.nomineeAddress} />
                    </div>
                    <div className="section-divider" />
                    <div className="nominee-header"><strong>Part B — EPS Family Members</strong></div>
                    <div className="review-row-5">
                        <ReviewItem label="Family Member Name" value={form2Data?.familyMemberName} />
                        <ReviewItem label="Family Address" value={form2Data?.familyAddress} />
                        <ReviewItem label="Date of Birth" value={formatDate(form2Data?.familyDob)} />
                        <ReviewItem label="Age" value={form2Data?.familyAge} />
                        <ReviewItem label="Relationship" value={form2Data?.familyRelationship} isCapitalized />
                    </div>
                    {form2Nominees.map((m, i) => (
                        <div key={m.id}>
                            <div className="nominee-header"><strong>Family Member {i + 2}</strong></div>
                            <div className="review-row-5">
                                <ReviewItem label="Name" value={m.familyMemberName} />
                                <ReviewItem label="Address" value={m.familyAddress} />
                                <ReviewItem label="Date of Birth" value={formatDate(m.familyDob)} />
                                <ReviewItem label="Age" value={m.familyAge} />
                                <ReviewItem label="Relationship" value={m.familyRelationship} isCapitalized />
                            </div>
                        </div>
                    ))}
                    <div className="section-divider" />
                    <div className="review-row-3">
                        <ReviewItem label="Subscriber Signature" value={null} isSigned={!!form2Data?.subscriberSignature} />
                        <ReviewItem label="Employer Signature" value={form2Data?.employerSignature} />
                        <ReviewItem label="Place" value={form2Data?.place} />
                    </div>
                    <div className="review-row-2">
                        <ReviewItem label="Date" value={formatDate(form2Data?.date)} />
                    </div>
                </div>
            </div>

            {/* ── GRATUITY ── */}
            <div className="review-section">
                <h2 className="section-title">Gratuity Nomination Form</h2>
                <div className="review-section-body">
                    <div className="review-row-3">
                        <ReviewItem label="To (Name)" value={gratuityData?.toName} />
                        <ReviewItem label="Address" value={gratuityData?.address} />
                    </div>
                    <div className="section-divider" />
                    <div className="nominee-header"><strong>Nominee I</strong></div>
                    <div className="review-row-5">
                        <ReviewItem label="Shri/Shrimati/Kumari" value={gratuityData?.prefix} />
                        <ReviewItem label="Name with full address" value={gratuityData?.nomineeFullName} />
                        <ReviewItem label="Full Name" value={gratuityData?.nomineeName} />
                        <ReviewItem label="Address" value={gratuityData?.nomineeAddress} />
                    </div>
                    <div className="review-row-5">
                        <ReviewItem label="Date of Birth" value={formatDate(gratuityData?.nomineeDob)} />
                        <ReviewItem label="Age" value={gratuityData?.nomineeAge} />
                        <ReviewItem label="Relationship" value={gratuityData?.relationship} isCapitalized />
                        <ReviewItem label="Proportion" value={withPercent(gratuityData?.proportion)} />
                    </div>
                    {gratuityNominees.map((n, i) => (
                        <div key={n.id}>
                            <div className="nominee-header"><strong>Nominee {i + 2}</strong></div>
                            <div className="review-row-5">
                                <ReviewItem label="Shri/Shrimati/Kumari" value={n.prefix} />
                                <ReviewItem label="Name with address" value={n.fullName} />
                                <ReviewItem label="Full Name" value={n.name} />
                                <ReviewItem label="Address" value={n.address} />
                            </div>
                            <div className="review-row-5">
                                <ReviewItem label="DOB" value={formatDate(n.dob)} />
                                <ReviewItem label="Age" value={n.age} />
                                <ReviewItem label="Relationship" value={n.relationship} isCapitalized />
                                <ReviewItem label="Proportion" value={withPercent(n.proportion)} />
                            </div>
                        </div>
                    ))}
                    <div className="section-divider" />
                    <div className="review-row-5">
                        <ReviewItem label="Employee Name" value={gratuityData?.employeeName} />
                        <ReviewItem label="Gender" value={gratuityData?.gender} isCapitalized />
                        <ReviewItem label="Religion" value={gratuityData?.religion} />
                        <ReviewItem label="Marital Status" value={gratuityData?.maritalStatus} isCapitalized />
                        <ReviewItem label="Department" value={gratuityData?.department} />
                    </div>
                    <div className="review-row-5">
                        <ReviewItem label="Post held" value={gratuityData?.postHeld} />
                        <ReviewItem label="Date of Appointment" value={formatDate(gratuityData?.appointmentDate)} />
                        <ReviewItem label="Permanent Address" value={gratuityData?.permanentAddress} />
                    </div>
                    <div className="review-row-3">
                        <ReviewItem label="Place" value={gratuityData?.place} />
                        <ReviewItem label="Date" value={formatDate(gratuityData?.date)} />
                        <ReviewItem label="Subscriber Signature" value={null} isSigned={!!gratuityData?.subscriberSignature} />
                    </div>
                    <div className="review-row-3">
                        <ReviewItem label="Joining Location" value={gratuityData?.joiningLocation} />
                        <ReviewItem label="Establishment Address" value={gratuityData?.establishmentAddress} />
                    </div>
                    <div className="review-row-3">
                        <ReviewItem label="Designation" value={gratuityData?.designation} />
                        <ReviewItem label="Date" value={formatDate(gratuityData?.finalDate)} />
                        <ReviewItem label="Employer Signature" value={gratuityData?.employerSignature} />
                    </div>
                </div>
            </div>

            {/* ── FORM 11 ── */}
            <div className="review-section">
                <h2 className="section-title">Form 11 — EPF/EPS Declaration</h2>
                <div className="review-section-body">
                    <div className="review-row-5">
                        <ReviewItem label="Member Name" value={form11Data?.memberName} />
                        <ReviewItem label="Father/Husband's Name" value={form11Data?.fatherName} />
                        <ReviewItem label="Date of Birth" value={formatDate(form11Data?.dob)} />
                        <ReviewItem label="Gender" value={form11Data?.gender} isCapitalized />
                        <ReviewItem label="Marital Status" value={form11Data?.maritalStatus} isCapitalized />
                    </div>
                    <div className="review-row-5">
                        <ReviewItem label="Email ID" value={form11Data?.email} />
                        <ReviewItem label="Mobile Number" value={form11Data?.mobile} />
                        <ReviewItem label="EPF Scheme Member" value={form11Data?.epfMember} isCapitalized />
                        <ReviewItem label="EPS Scheme Member" value={form11Data?.epsMember} isCapitalized />
                        <ReviewItem label="Universal Account Number" value={form11Data?.uan} />
                    </div>
                    <div className="review-row-5">
                        <ReviewItem label="Exit Date" value={formatDate(form11Data?.exitDate)} />
                        <ReviewItem label="International Worker" value={form11Data?.internationalWorker} isCapitalized />
                        {form11Data?.internationalWorker === 'yes' && (
                            <>
                                <ReviewItem label="Country of Origin" value={form11Data?.country} />
                                <ReviewItem label="Passport Number" value={form11Data?.passport} />
                                <ReviewItem label="Validity of Passport" value={form11Data?.passportValidity} />
                            </>
                        )}
                    </div>
                    <div className="review-row-5">
                        <ReviewItem label="Bank Account" value={form11Data?.bankAccount} />
                        <ReviewItem label="IFSC Code" value={form11Data?.ifsc} />
                        <ReviewItem label="Aadhar Number" value={form11Data?.aadhar} />
                        <ReviewItem label="PAN Number" value={form11Data?.pan} />
                    </div>
                    <div className="review-row-3">
                        <ReviewItem label="Place" value={form11Data?.place} />
                        <ReviewItem label="Date" value={formatDate(form11Data?.date)} />
                    </div>
                    <div className="section-divider" />
                    <div className="review-row-4">
                        <ReviewItem label="Member Signature" value={null} isSigned={!!form11Data?.memberSignature} />
                        <ReviewItem label="Member's Name" value={form11Data?.memberNameConfirm} />
                        <ReviewItem label="Joined On" value={formatDate(form11Data?.joinedOn)} />
                        <ReviewItem label="Date" value={formatDate(form11Data?.signatureDate)} />
                    </div>
                    <div className="review-row-full">
                        <ReviewItem label="Employer/Authorised Officer Signature" value={form11Data?.employerSignature} />
                    </div>
                </div>
            </div>

            {/* ── FORM L — Only for female employees ── */}
            {isFemale && formLData && (
                <div className="review-section" style={{ '--section-gradient': 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)' } as React.CSSProperties}>
                    <h2 className="section-title" style={{ background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)' }}>
                        Form L — Consent to Work in Night Shift
                    </h2>
                    <div className="review-section-body">
                        <div className="review-row-3">
                            <ReviewItem label="Full Name" value={formLData?.fullName} />
                            <ReviewItem label="Designation" value={formLData?.designation} />
                            <ReviewItem label="Date of Joining" value={formatDate(formLData?.dateOfJoining)} />
                        </div>
                        <div className="review-row-2">
                            <ReviewItem label="Full Address" value={formLData?.fullAddress} />
                            <ReviewItem label="Working Location" value={formLData?.workingLocation} />
                        </div>
                        <div className="section-divider" />
                        <div className="review-row-3">
                            <ReviewItem
                                label="POSH Committee Chairperson"
                                value={
                                    formLData?.poshChairpersonName
                                        ? `Smt. ${formLData.poshChairpersonName}, TTEC ${formLData.workingLocation}`
                                        : `TTEC ${formLData?.workingLocation}`
                                }
                            />                            <ReviewItem label="Night Shift Period" value="9:00 PM – 7:00 AM" />
                            <ReviewItem label="Employee Signature" value={null} isSigned={!!formLData?.employeeSignature} />
                        </div>
                        <div className="review-row-2">
                            <ReviewItem
                                label="Witness 1 Signature"
                                value={null}
                                isSigned={!!formLData?.witness1Signature}
                            />
                            <ReviewItem
                                label="Witness 2 Signature"
                                value={null}
                                isSigned={!!formLData?.witness2Signature}
                            />
                        </div>
                    </div>
                </div>
            )}

            <div className="review-actions">
                <Button label="Back" onClick={onBack} className="back-btn p-button-outlined" />
                <Button label="Submit" onClick={() => setShowConfirmDialog(true)} className="submit-btn p-button-primary" />
            </div>

            {/* ── Confirm Dialog ── */}
            <Dialog
                visible={showConfirmDialog}
                onHide={() => {
                    if (submitState !== 'pending') {
                        setShowConfirmDialog(false);
                        setDobConfirm(undefined);
                        setDobError('');
                        setSubmitSignature('');
                    }
                }}
                closable={submitState !== 'pending'}
                header={<div className="dialog-header"><div className="warning-icon">!</div><h3>Review &amp; Submit</h3></div>}
                modal className="confirmation-dialog" style={{ width: '480px' }}
            >
                <div className="dialog-content">
                    <div className="dialog-message">
                        <input type="checkbox" checked={isConfirmed} onChange={(e) => setIsConfirmed(e.target.checked)} />
                        <span>I confirm all information is accurate and authorize TTEC to process these forms with authorities.</span>
                    </div>
                    <div className="dialog-inputs">
                        <div className="input-group">
                            <label>Full Name</label>
                            <input
                                type="text"
                                placeholder="Enter your full name"
                                value={employeeName}
                                onChange={(e) => setEmployeeName(e.target.value)}
                            />
                        </div>
                        <div className="input-group">
                            <label>
                                Date of Birth
                                <span className="dialog-security-note">identity verification</span>
                            </label>
                            <Calendar
                                value={dobConfirm}
                                onChange={(e) => {
                                    const val = (e.value as Date) ?? undefined;
                                    setDobConfirm(val);
                                    if (dobError) setDobError('');
                                    if (val) validateDobConfirm(val);
                                }}
                                placeholder="Select your date of birth"
                                showIcon dateFormat="dd/mm/yy" readOnlyInput maxDate={new Date()}
                                className={`dialog-dob-calendar${dobError ? ' p-invalid' : ''}`}
                            />
                            {dobError && <small className="dialog-field-error">{dobError}</small>}
                        </div>
                        <div className="input-group">
                            <label>Signature Confirmation</label>
                            <SignatureInput
                                value={submitSignature}
                                onChange={(v) => setSubmitSignature(v)}
                                placeholder="Type your full name to sign"
                            />
                        </div>
                    </div>
                    <p className="secure-text">🔒 Secure and Encrypted</p>
                    <Button
                        label="Submit all forms"
                        onClick={handleConfirmSubmit}
                        className="dialog-submit-btn"
                        disabled={!isConfirmed || !employeeName || !dobConfirm || !submitSignature.trim() || submitState === 'pending'}
                        loading={submitState === 'pending'}
                    />
                </div>
            </Dialog>

            {/* ── Success Dialog ── */}
            <Dialog
                visible={showSuccessDialog} onHide={() => { }} closable={false} closeOnEscape={false}
                header={
                    <div className="dialog-header success">
                        <div className="success-icon">✓</div>
                        <h3>Submission Successful!</h3>
                    </div>
                }
                modal className="success-dialog" style={{ width: '500px' }}
            >
                <div className="dialog-content">
                    <p className="success-message">
                        Thank you, <strong>{employeeName || getValue(form2Data?.fullName)}!</strong>
                    </p>
                    <p className="success-submessage">
                        Your Statutory forms have been submitted on{' '}
                        <strong>
                            {submissionTime
                                ? new Intl.DateTimeFormat('en-IN', { month: 'long', day: 'numeric', year: 'numeric' }).format(submissionTime)
                                : 'Month Day, Year'}
                        </strong>, at{' '}
                        <strong>
                            {submissionTime
                                ? new Intl.DateTimeFormat('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }).format(submissionTime) + ' IST'
                                : '00:00 pm IST'}
                        </strong>
                    </p>
                    <p className="confirmation-text">Confirmation Email Sent.</p>
                    <Button label="Close" onClick={handleSuccessClose} className="dialog-close-btn" />
                </div>
            </Dialog>
        </div>
    );
};

export default Review;
